import { TemplateScene } from '@/types/cmsTypes';
import { TemplateSceneCard } from './template-scene-card';
import { Pagination } from '@/components/cms/ui/pagination';
import { SearchField } from '@/components/ui/search-field';
import { CollapsibleHeading } from '@/components/ui/disclosure';
import Filter from '@/common/Filter';
import { TEMPLATE_FEATURES, templateFeatureLabel } from '@/components/publish-flow/templateOptions';
import SortSelect from '@/common/SortSelect';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { EmptyState } from '@/components/ui/empty-state';
import { Card } from '@/components/ui/card';
import LureoButton from '@/common/LureoButton';

interface TemplatesCarouselProps {
  templates: TemplateScene[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onSelectTemplate: (template: TemplateScene) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  difficultyFilter: 'all' | 'beginner' | 'intermediate' | 'advanced';
  onDifficultyFilterChange: (filter: 'all' | 'beginner' | 'intermediate' | 'advanced') => void;
  featureFilter: string;
  onFeatureFilterChange: (filter: string) => void;
  sortOrder: 'rank' | 'asc' | 'desc' | 'newest' | 'oldest';
  onSortChange: (order: 'rank' | 'asc' | 'desc' | 'newest' | 'oldest') => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export function TemplatesCarousel({ 
  templates, 
  loading = false,
  error = null,
  onRetry,
  onSelectTemplate,
  searchQuery,
  onSearchChange,
  difficultyFilter,
  onDifficultyFilterChange,
  featureFilter,
  onFeatureFilterChange,
  sortOrder,
  onSortChange,
  currentPage,
  totalPages,
  onPageChange,
  isExpanded,
  onToggleExpand,
}: TemplatesCarouselProps) {
  const difficultyOptions = [
    { value: 'all', label: 'All Difficulty' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const featureOptions = [
    { value: 'all', label: 'All Features' },
    ...TEMPLATE_FEATURES.map((f) => ({ value: f.key, label: f.label })),
  ];

  return (
    <div className="w-full">
      {/* Desktop Header with Search and Filters */}
      <div className="mb-6 hidden items-center justify-between lg:flex">
        <CollapsibleHeading title="Template Stories" open={isExpanded} onOpenChange={onToggleExpand} />
        {isExpanded && (
          <div className="flex items-center gap-3">
            <SearchField
              size="md"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={onSearchChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              className="w-64"
            />
            <Filter<'all' | 'beginner' | 'intermediate' | 'advanced'>
              options={difficultyOptions}
              onFilterChange={onDifficultyFilterChange}
              placeholder="Filter by difficulty"
              value={difficultyFilter}
            />
            <Filter<string>
              options={featureOptions}
              onFilterChange={onFeatureFilterChange}
              placeholder="Filter by feature"
              value={featureFilter}
            />
            <SortSelect
              value={sortOrder}
              onValueChange={onSortChange}
              options={['rank', 'newest', 'oldest', 'asc', 'desc']}
            />
          </div>
        )}
      </div>

      {/* Mobile/Tablet Header - Simple */}
      <div className="mb-4 lg:hidden">
        <CollapsibleHeading title="Templates" open={isExpanded} onOpenChange={onToggleExpand} />
        {isExpanded && (
          <>
            {searchQuery && <p className="mt-1 text-lureo-14 text-lureo-ink-subtle leading-5">Search: "{searchQuery}"</p>}
            {difficultyFilter !== 'all' && <p className="mt-1 text-lureo-14 text-lureo-ink-subtle leading-5">Filter: {difficultyFilter}</p>}
            {featureFilter !== 'all' && <p className="mt-1 text-lureo-14 text-lureo-ink-subtle leading-5">Feature: {templateFeatureLabel(featureFilter)}</p>}
            {sortOrder !== 'rank' && <p className="mt-1 text-lureo-14 text-lureo-ink-subtle leading-5">Sort: {sortOrder === 'asc' ? 'A to Z' : sortOrder === 'desc' ? 'Z to A' : sortOrder === 'oldest' ? 'Oldest' : 'Newest'}</p>}
          </>
        )}
      </div>

      {/* Templates Grid */}
      {isExpanded && (
        <>
      {loading && templates.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="xl" />
        </div>
      ) : error && templates.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            title="Couldn't load the templates"
            action={onRetry && <LureoButton variant="secondary" suppressTooltip onClick={onRetry} className="h-9 rounded-lureo-radius-button-pill px-4">Try again</LureoButton>}
          >
            Check your connection and try again.
          </EmptyState>
        </Card>
      ) : templates.length === 0 ? (
        <EmptyState size="roomy">No templates found. Try adjusting your search or filter criteria.</EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-4 lg:gap-8">
            {templates.map((template) => (
              <TemplateSceneCard key={template.id} template={template} onSelect={onSelectTemplate} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          )}
        </>
      )}
        </>
      )}
    </div>
  );
}

